import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";

import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL } from "../../constants";
import { authHeader } from "../../utils/auth";
import { ExportLibraryLoadError, loadZipLibrary } from "../../utils/exportLibraries";
import {
  isUsableTouchup,
  touchupFailureMessage,
  touchupSettlementMessage,
  touchupVerificationLabel,
} from "./touchupResults";

/**
 * @typedef {Object} TouchupEdit
 * @property {string} edit_type
 * @property {string} [summary]
 *
 * @typedef {Object} TouchupPhoto
 * @property {string} filename
 * @property {string | null} original_url
 * @property {string | null} edited_url
 * @property {TouchupEdit[] | string[]} edits
 * @property {string} severity
 * @property {"pending" | "done" | "failed"} state
 * @property {string | null} error
 * @property {any} [verification]
 * @property {any} [last_attempt]
 *
 * @typedef {Object} TouchupRoom
 * @property {string} room
 * @property {string} room_label
 * @property {TouchupPhoto[]} photos
 *
 * @typedef {Object} TouchupSet
 * @property {string} uuid
 * @property {"pending" | "done" | "partial" | "failed"} state
 * @property {string | null} address
 * @property {TouchupRoom[]} rooms
 * @property {number} total_photos
 * @property {number} succeeded_photos
 * @property {string} created_at
 * @property {string} updated_at
 * @property {boolean} [is_owner]
 * @property {string} [computation_uuid]
 */

const POLL_INTERVAL_MS = 20000;

/** @type {Record<string, string>} */
const TOUCHUP_ACCESS_MESSAGES = {
  forbidden: "This photo link was revoked, expired, or replaced. Ask the agent for a current link.",
  not_found:
    "This touch-up page could not be found. Check the link or ask the agent for a current link.",
  stale: "These photos changed after this link was created. Ask the agent for a current link.",
  error:
    "We couldn't load your photos. Please refresh the page or email help@listino.ai for assistance.",
};

/**
 * Keep capability parsing local to the current URL. The raw bearer token is
 * never persisted, rendered, or sent to analytics.
 *
 * @param {string} search
 */
const shareTokenFromSearch = (search) => {
  try {
    return new URLSearchParams(search).get("share_token") || "";
  } catch {
    return "";
  }
};

/** @param {string} uuid @param {string} shareToken */
const touchupEndpoint = (uuid, shareToken) => {
  const base = `${API_URL}/touchups/${encodeURIComponent(uuid)}/`;
  return shareToken ? `${base}?share_token=${encodeURIComponent(shareToken)}` : base;
};

/**
 * Map only response status, never backend text, into recipient-safe UI state.
 *
 * @param {unknown} error
 * @returns {"forbidden" | "not_found" | "stale" | "error"}
 */
const statusForTouchupError = (error) => {
  const typed = /** @type {{response?: {status?: unknown}}} */ (error);
  if (typed.response?.status === 403) return "forbidden";
  if (typed.response?.status === 404) return "not_found";
  if (typed.response?.status === 409) return "stale";
  return "error";
};

/** @type {Record<string, string>} */
const EDIT_LABELS = {
  declutter: "Decluttered",
  floor_scuff: "Floor marks removed",
  remove_outdoor_debris: "Yard debris removed",
  green_up_grass: "Grass greened up",
  sunny_day: "Brightened to sunny",
  brighten_room: "Room brightened",
  touch_up_landscaping: "Landscaping tidied",
};

/** Edit types are open-ended (the worker can emit new slugs) — unknown ones
 *  must still read as words, never raw snake_case. */
const editLabel = (/** @type {string} */ slug) => {
  if (EDIT_LABELS[slug]) return EDIT_LABELS[slug];
  const words = (slug || "").replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : slug;
};

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 60px;

  @media (max-width: 640px) {
    padding: 100px 0 40px;
  }
`;

const Eyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin: 0 0 8px 0;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #4a4a4a;
  margin: 0 0 16px 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 40px 0;
`;

const DownloadAllButton = styled.button`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  background: #1a1a1a;
  border: 1px solid #1a1a1a;
  border-radius: 999px;
  padding: 10px 22px;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: #000000;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-height: 44px;
  }
`;

const StatusBanner = styled.div`
  background: ${(/** @type {{ $kind?: string }} */ p) => (p.$kind === "error" ? "#fef2f2" : "#eff6ff")};
  border: 1px solid ${(/** @type {{ $kind?: string }} */ p) => (p.$kind === "error" ? "#fecaca" : "#dbeafe")};
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 32px;
  color: ${(/** @type {{ $kind?: string }} */ p) => (p.$kind === "error" ? "#991b1b" : "#1e40af")};

  h3 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 4px 0;
  }

  p {
    font-size: 0.9rem;
    margin: 0;
  }
`;

const Disclaimer = styled.aside`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 32px;
  color: #78350f;
  font-size: 0.85rem;
  line-height: 1.5;

  strong {
    font-weight: 600;
  }

  p {
    margin: 0;
  }

  p + p {
    margin-top: 8px;
  }
`;

const RoomSection = styled.section`
  margin-bottom: 56px;
`;

const RoomHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 28px;
`;

const PhotoRow = styled.div`
  margin-bottom: 40px;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ImageCard = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 12px;
  background-color: #f3f4f6;
  border: ${(/** @type {{ $highlight?: boolean }} */ p) => (p.$highlight ? "none" : "1px solid #e5e7eb")};
  box-shadow: ${(/** @type {{ $highlight?: boolean }} */ p) =>
    p.$highlight ? "0 0 0 2px #1a1a1a, 0 0 0 4px #ffffff" : "none"};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 0.9rem;
`;

const ImageLabel = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${(/** @type {{ $highlight?: boolean }} */ p) => (p.$highlight ? "#1a1a1a" : "rgba(255, 255, 255, 0.9)")};
  color: ${(/** @type {{ $highlight?: boolean }} */ p) => (p.$highlight ? "#ffffff" : "#374151")};
`;

const PhotoFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
  padding: 4px 12px;
  border-radius: 999px;
`;

const EditList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EditRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  font-size: 0.875rem;
  color: #4a4a4a;
`;

const EditSummary = styled.span`
  color: #6b7280;
`;

const DownloadButton = styled.button`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  padding: 8px 20px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FailedPhoto = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  color: #4a4a4a;

  strong {
    display: block;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  p {
    font-size: 0.875rem;
    margin: 0;
  }
`;

const CenteredMessage = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #4a4a4a;

  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 12px;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }

  a {
    color: #1a1a1a;
    text-decoration: underline;
  }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** @param {string | undefined | null} room */
const formatRoomLabel = (room) => {
  if (!room) return "Other";
  return room.replace(/_/g, " ").replace(/\b\w/g, (/** @type {string} */ c) => c.toUpperCase());
};

/**
 * @param {string} address
 */
const slugifyForZip = (address) => {
  const cleaned = (address || "listing")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return cleaned || "listing";
};

/**
 * Fetch every successful edited photo, zip them client-side, trigger download.
 *
 * @param {TouchupSet} data
 * @param {(msg: string) => void} onProgress
 */
const downloadAll = async (data, onProgress, isCurrent = () => true) => {
  const photos = (data.rooms || [])
    .flatMap((room) =>
      (room.photos || []).map((photo) => ({ photo, room: room.room_label || room.room || "other" }))
    )
    .filter(({ photo }) => isUsableTouchup(photo));

  if (photos.length === 0) return;

  const JSZip = await loadZipLibrary();
  if (!isCurrent()) return;
  const zip = new JSZip();
  let completed = 0;
  onProgress(`Fetching 0 of ${photos.length}…`);

  const fetches = photos.map(async ({ photo, room }) => {
    const url = photo.edited_url;
    if (!url) return;
    const response = await fetch(url, { cache: "reload" });
    if (!isCurrent()) return;
    if (!response.ok) throw new Error(`Failed to fetch ${photo.filename}`);
    const blob = await response.blob();
    if (!isCurrent()) return;
    const folder = slugifyForZip(room);
    zip.file(`${folder}/${photo.filename}`, blob);
    completed += 1;
    onProgress(`Fetching ${completed} of ${photos.length}…`);
  });

  await Promise.all(fetches);
  if (!isCurrent()) return;
  onProgress("Building zip…");
  const zipBlob = await zip.generateAsync({ type: "blob" });
  if (!isCurrent()) return;

  const blobUrl = URL.createObjectURL(zipBlob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `${slugifyForZip(data.address || "listing")}-touched-up-photos.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

/**
 * @param {string} url
 * @param {string} filename
 */
const downloadImage = async (url, filename, isCurrent = () => true) => {
  try {
    const response = await fetch(url, { cache: "reload" });
    if (!isCurrent()) return;
    if (!response.ok) throw new Error("image unavailable");
    const blob = await response.blob();
    if (!isCurrent()) return;
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    if (isCurrent()) window.open(url, "_blank", "noopener,noreferrer");
  }
};

const TouchupsPage = () => {
  /** @type {{ uuid: string }} */
  const { uuid } = useParams();
  const location = useLocation();
  const shareToken = shareTokenFromSearch(location.search);
  const endpoint = touchupEndpoint(uuid, shareToken);
  // Sellers never see credits: the refund facts below render only for the
  // owner's own view of the gallery (no share token in the URL).
  const ownerRequest = !shareToken;
  const activeEndpointRef = useRef(endpoint);
  activeEndpointRef.current = endpoint;
  const loadedEndpointRef = useRef(/** @type {string | null} */ (null));
  const [dataState, setData] = useState(/** @type {TouchupSet | null} */ (null));
  const [statusState, setStatus] = useState(
    /** @type {"loading" | "ok" | "forbidden" | "not_found" | "stale" | "error"} */ ("loading")
  );
  const routeStateIsCurrent = loadedEndpointRef.current === endpoint;
  const data = routeStateIsCurrent ? dataState : null;
  const ownerCreditsView = ownerRequest && data?.is_owner === true;
  const status = routeStateIsCurrent ? statusState : "loading";
  const [zipStatus, setZipStatus] = useState(/** @type {string | null} */ (null));
  const [actionBusy, setActionBusy] = useState(/** @type {string | null} */ (null));
  const [actionError, setActionError] = useState("");
  const zipFlightRef = useRef(/** @type {object | null} */ (null));
  const fetchGeneration = useRef(0);

  const requestData = useCallback(async () => {
    // Axios retains the owner's normal Authorization header behavior. A public
    // recipient gets the same request plus the URL-encoded, in-memory token.
    const response = await axios.get(endpoint, { headers: authHeader() });
    return /** @type {TouchupSet} */ (response.data);
  }, [endpoint]);

  const fetchData = useCallback(async () => {
    const generation = ++fetchGeneration.current;
    const requestEndpoint = endpoint;
    try {
      const freshData = await requestData();
      if (generation !== fetchGeneration.current || activeEndpointRef.current !== requestEndpoint)
        return;
      setData(freshData);
      setStatus("ok");
    } catch (error) {
      if (generation !== fetchGeneration.current || activeEndpointRef.current !== requestEndpoint)
        return;
      const failureStatus = statusForTouchupError(error);
      // Authorization/revocation/version failures are terminal for this URL.
      // Clear stale content immediately; doing so also tears down the pending
      // poll loop instead of keeping a revoked gallery visible forever.
      if (failureStatus !== "error") {
        setData(null);
        setStatus(failureStatus);
        return;
      }
      // A transient error mid-poll must not blank an already-loaded gallery —
      // that also tore down the pending-state refresh loop permanently. Only
      // a failure BEFORE any data arrives becomes an error screen.
      setData((prev) => {
        if (prev) return prev;
        setStatus("error");
        return null;
      });
    }
  }, [endpoint, requestData]);

  useEffect(() => {
    loadedEndpointRef.current = endpoint;
    zipFlightRef.current = null;
    setData(null);
    setStatus("loading");
    setZipStatus(null);
    setActionBusy(null);
    setActionError("");
    fetchData();
    return () => {
      fetchGeneration.current += 1;
      zipFlightRef.current = null;
    };
  }, [endpoint, fetchData]);

  useEffect(() => {
    if (data?.state !== "pending") return undefined;
    let cancelled = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let handle;
    const poll = async () => {
      await fetchData();
      if (!cancelled) handle = setTimeout(poll, POLL_INTERVAL_MS);
    };
    handle = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (handle) clearTimeout(handle);
    };
  }, [data?.state, fetchData]);

  const handleRefreshFailure = useCallback((/** @type {unknown} */ error) => {
    const failureStatus = statusForTouchupError(error);
    if (failureStatus === "error") {
      setActionError("Could not refresh the photos. Check your connection, then try again.");
      return;
    }
    setData(null);
    setStatus(failureStatus);
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (!data || actionBusy || zipFlightRef.current) return;
    const flight = {};
    zipFlightRef.current = flight;
    const requestEndpoint = endpoint;
    const authorization = authHeader().Authorization;
    const isCurrent = () =>
      activeEndpointRef.current === requestEndpoint &&
      zipFlightRef.current === flight &&
      authHeader().Authorization === authorization;
    const finish = () => {
      if (zipFlightRef.current === flight) {
        zipFlightRef.current = null;
        setZipStatus(null);
        setActionBusy(null);
      }
    };
    setActionBusy("all");
    setActionError("");
    setZipStatus("Refreshing photos…");

    let freshData;
    try {
      freshData = await requestData();
      if (!isCurrent()) {
        finish();
        return;
      }
      setData(freshData);
      setStatus("ok");
    } catch (error) {
      if (isCurrent()) handleRefreshFailure(error);
      finish();
      return;
    }

    try {
      await downloadAll(
        freshData,
        (message) => {
          if (isCurrent()) setZipStatus(message);
        },
        isCurrent
      );
    } catch (error) {
      if (isCurrent()) {
        setActionError(
          error instanceof ExportLibraryLoadError
            ? error.message
            : "Download failed. Please try the individual photo buttons instead."
        );
      }
    } finally {
      finish();
    }
  }, [actionBusy, data, endpoint, handleRefreshFailure, requestData]);

  /** @param {string} roomKey @param {string} filename */
  const handleDownloadImage = useCallback(
    async (/** @type {string} */ roomKey, /** @type {string} */ filename) => {
      if (actionBusy) return;
      const requestEndpoint = endpoint;
      const isCurrent = () => activeEndpointRef.current === requestEndpoint;
      const busyKey = `photo:${roomKey}:${filename}`;
      setActionBusy(busyKey);
      setActionError("");

      let freshData;
      try {
        freshData = await requestData();
        if (!isCurrent()) return;
        setData(freshData);
        setStatus("ok");
      } catch (error) {
        if (!isCurrent()) return;
        handleRefreshFailure(error);
        setActionBusy(null);
        return;
      }

      const room = (freshData.rooms || []).find((candidate) => candidate.room === roomKey);
      const photo = (room?.photos || []).find((candidate) => candidate.filename === filename);
      if (!photo || !isUsableTouchup(photo) || !photo.edited_url) {
        setActionError(
          "That edited photo is no longer available. Refresh the page or ask the agent for help."
        );
        setActionBusy(null);
        return;
      }

      await downloadImage(photo.edited_url, photo.filename, isCurrent);
      if (isCurrent()) setActionBusy(null);
    },
    [actionBusy, endpoint, handleRefreshFailure, requestData]
  );

  const visibleRooms = (data?.rooms || [])
    .map((room) => ({
      ...room,
      photos: ownerCreditsView ? room.photos : room.photos.filter(isUsableTouchup),
    }))
    .filter((room) => room.photos.length > 0);
  const availableCount = visibleRooms.flatMap((room) => room.photos).filter(isUsableTouchup).length;
  const failedAttempts = ownerCreditsView
    ? visibleRooms
        .flatMap((room) => room.photos)
        .filter((photo) => photo.state === "failed" || photo.last_attempt?.status === "failed")
        .length
    : 0;
  const retryPath =
    ownerCreditsView && data?.computation_uuid
      ? `/reports/${encodeURIComponent(data.computation_uuid)}/edit#add-to-listing`
      : "";

  let body;
  if (status === "loading") {
    body = (
      <CenteredMessage>
        <Spinner />
        <p>Loading your photos…</p>
      </CenteredMessage>
    );
  } else if (["forbidden", "not_found", "stale"].includes(status)) {
    body = (
      <CenteredMessage role="alert">
        <h1>This photo link isn't available</h1>
        <p>{TOUCHUP_ACCESS_MESSAGES[status]}</p>
      </CenteredMessage>
    );
  } else if (status === "error" || !data) {
    body = (
      <CenteredMessage role="alert">
        <h1>Something went wrong</h1>
        <p>
          {TOUCHUP_ACCESS_MESSAGES.error} <a href="mailto:help@listino.ai">Contact support</a>.
        </p>
      </CenteredMessage>
    );
  } else {
    body = (
      <>
        <Eyebrow>Touched-up photos</Eyebrow>
        <Title>{data.address || "Your listing"}</Title>
        <Subtitle>
          {availableCount} edited photo{availableCount === 1 ? "" : "s"} available. These are
          optional — compare them with your originals before deciding what to use.
        </Subtitle>
        {availableCount > 0 && (
          <HeaderActions>
            <output aria-live="polite" style={{ fontSize: "0.875rem", color: "#6b7280" }}>
              {zipStatus || `${availableCount} photos available`}
            </output>
            <DownloadAllButton
              type="button"
              onClick={handleDownloadAll}
              disabled={actionBusy !== null}
            >
              {zipStatus ? "Preparing…" : `Download all (${availableCount})`}
            </DownloadAllButton>
          </HeaderActions>
        )}

        {actionError && (
          <StatusBanner $kind="error" role="alert">
            <h3>Download unavailable</h3>
            <p>{actionError}</p>
          </StatusBanner>
        )}

        {data.state === "pending" && (
          <StatusBanner as="output" aria-live="polite">
            <h3>Preparing your photos</h3>
            <p>
              Your touched-up photos are still generating. This page will refresh automatically when
              they're ready — usually within 15 minutes.
            </p>
          </StatusBanner>
        )}
        {ownerCreditsView && (failedAttempts > 0 || data.state === "failed") && (
          <StatusBanner $kind="error" aria-live="polite">
            <h3>Some touch-up attempts need attention</h3>
            <p>
              No new edit is delivered for a failed attempt. Your originals and any earlier
              available edits are unchanged. Review the results below before trying again.
            </p>
            {retryPath && (
              <p>
                <Link to={retryPath}>Review retry options</Link> — choose photos and review the cost
                before starting another attempt.
              </p>
            )}
          </StatusBanner>
        )}

        {availableCount > 0 && (
          <Disclaimer role="note">
            <p>
              <strong>Compare with the original before use.</strong> Automated checks are not a
              guarantee of accuracy. Check that property features, condition, and materials are
              represented correctly.
            </p>
            <p>
              <strong>Before you use these photos:</strong> these are AI-enhanced versions of your
              originals. Most MLS boards and the NAR Code of Ethics (Articles 2 and 12) require that
              altered or virtually-staged listing photos be clearly disclosed — typically with a
              "Virtually Staged" or "Digitally Enhanced" label, and by keeping the unaltered
              original available alongside.
            </p>
            <p>
              Rules vary by MLS, brokerage, and state (for example, California's AB 723 took effect
              in January 2026). Before publishing these images, check your local MLS rules, your
              brokerage's policies, and applicable state and federal law to confirm they're
              permitted and properly disclosed for your listing.
            </p>
          </Disclaimer>
        )}

        {visibleRooms.length === 0 ? (
          <CenteredMessage>
            <p>No photos to show yet.</p>
          </CenteredMessage>
        ) : (
          visibleRooms.map((/** @type {TouchupRoom} */ room, /** @type {number} */ roomIdx) => (
            <RoomSection key={room.room || roomIdx}>
              <RoomHeading>{room.room_label || formatRoomLabel(room.room)}</RoomHeading>
              {room.photos.map(
                (/** @type {TouchupPhoto} */ photo, /** @type {number} */ photoIdx) => (
                  <PhotoEntry
                    key={photo.filename || photoIdx}
                    photo={photo}
                    disabled={actionBusy !== null}
                    downloading={actionBusy === `photo:${room.room}:${photo.filename}`}
                    ownerCreditsView={ownerCreditsView}
                    retryPath={retryPath}
                    onDownload={() => handleDownloadImage(room.room, photo.filename)}
                  />
                )
              )}
            </RoomSection>
          ))
        )}
      </>
    );
  }

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container style={{ maxWidth: 960 }}>{body}</Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

/** @param {{ photo: TouchupPhoto, disabled: boolean, downloading: boolean, ownerCreditsView?: boolean, retryPath?: string, onDownload: () => void }} props */
const PhotoEntry = ({
  photo,
  disabled,
  downloading,
  ownerCreditsView = false,
  retryPath = "",
  onDownload,
}) => {
  const usable = isUsableTouchup(photo);
  const latestFailure =
    ownerCreditsView && photo.last_attempt?.status === "failed" ? photo.last_attempt : null;
  const failed =
    ownerCreditsView &&
    (photo.state === "failed" || latestFailure || (photo.edited_url && !usable));
  if (!ownerCreditsView && !usable) return null;
  const normalizedEdits = usable
    ? (photo.edits || []).map((raw) => {
        if (typeof raw === "string") return { edit_type: raw, label: editLabel(raw), summary: "" };
        const editType = raw?.edit_type || "";
        return { edit_type: editType, label: editLabel(editType), summary: raw?.summary || "" };
      })
    : [];

  return (
    <PhotoRow>
      {failed && (
        <FailedPhoto aria-live="polite">
          <strong>{photo.filename} — latest attempt not delivered</strong>
          <p>{touchupFailureMessage(latestFailure?.failure_code)} Your original is unchanged.</p>
          {usable && (
            <p>
              The earlier edited photo is still available below; it is not a new result from this
              attempt.
            </p>
          )}
          <p>{touchupSettlementMessage(latestFailure?.settlement)}</p>
          {retryPath && latestFailure?.retryable !== false && (
            <p>
              <Link to={retryPath} aria-label={`Review retry options for ${photo.filename}`}>
                Review retry options
              </Link>
            </p>
          )}
        </FailedPhoto>
      )}
      <ComparisonGrid>
        <div>
          <ImageCard>
            <ImageLabel>Before</ImageLabel>
            {photo.original_url ? (
              <img src={photo.original_url} alt="Before" />
            ) : (
              <ImagePlaceholder>Not available</ImagePlaceholder>
            )}
          </ImageCard>
        </div>
        <div>
          <ImageCard $highlight={usable}>
            <ImageLabel $highlight={usable}>
              {usable && latestFailure ? "After (previous result)" : "After"}
            </ImageLabel>
            {usable && photo.edited_url ? (
              <img src={photo.edited_url} alt="After" />
            ) : (
              <ImagePlaceholder>No new edited photo</ImagePlaceholder>
            )}
          </ImageCard>
        </div>
      </ComparisonGrid>
      {usable && (
        <p role="note">
          {touchupVerificationLabel(photo.verification)}. Compare with the original before use.
        </p>
      )}
      <PhotoFooter>
        {normalizedEdits.some((e) => e.summary) ? (
          <EditList>
            {normalizedEdits.map((edit, i) => (
              <EditRow key={edit.edit_type || i}>
                <Chip>{edit.label}</Chip>
                {edit.summary && <EditSummary>{edit.summary}</EditSummary>}
              </EditRow>
            ))}
          </EditList>
        ) : (
          <ChipRow>
            {normalizedEdits.map((edit, i) => (
              <Chip key={edit.edit_type || i}>{edit.label}</Chip>
            ))}
          </ChipRow>
        )}
        {usable && (
          <DownloadButton type="button" onClick={onDownload} disabled={disabled}>
            {downloading ? "Preparing…" : "Download"}
          </DownloadButton>
        )}
      </PhotoFooter>
    </PhotoRow>
  );
};

export default TouchupsPage;
